import { useDeferredValue, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink, GitBranch, Loader2, Search } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DeployableWorkflow } from "@/types/dialer/workflow";

export type PickedVersion = {
    workflowCode: string;
    workflowName: string;
    versionId: number;
    versionNumber: number;
};

export type PickVersionDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The slot being filled, named in the heading so the choice is unambiguous. */
    slot: string;
    workflows: DeployableWorkflow[] | null;
    loading?: boolean;
    error?: string | null;
    currentVersionId?: number | null;
    workflowCode?: string;
    workflowName?: string;
    onSwitchWorkflow?: () => void;
    onRetry?: () => void;
    onPick: (picked: PickedVersion) => void;
};

type WorkflowRowProps = {
    workflow: DeployableWorkflow;
    currentVersionId?: number | null;
    onPick: (picked: PickedVersion) => void;
};

function pick(workflow: DeployableWorkflow, version: DeployableWorkflow["versions"][number]): PickedVersion {
    return {
        workflowCode: workflow.workflow_code,
        workflowName: workflow.workflow_name,
        versionId: version.id,
        versionNumber: version.version
    };
}

function WorkflowRow({ workflow, currentVersionId, onPick }: WorkflowRowProps) {
    const [olderOpen, setOlderOpen] = useState(false);
    const versions = useMemo(() => [...workflow.versions].sort((left, right) => right.version - left.version), [workflow.versions]);
    const latest = versions[0];
    const older = versions.slice(1);
    const label = workflow.workflow_name || workflow.workflow_code;

    return (
        <li className="px-3 py-3 sm:px-4">
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-1.5">
                            <p className="truncate text-sm font-medium" title={label}>
                                {label}
                            </p>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground" asChild>
                                        <Link to={`/outils/campagnes/workflows/${workflow.workflow_code}`} aria-label={`Open ${label} lineage`}>
                                            <ExternalLink className="h-4 w-4" aria-hidden />
                                        </Link>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Open lineage</TooltipContent>
                            </Tooltip>
                        </div>
                        <p className="truncate font-mono text-xs text-muted-foreground">{workflow.workflow_code}</p>
                    </div>
                </div>

                {latest ? (
                    <Button variant={latest.id === currentVersionId ? "secondary" : "outline"} size="sm" className="w-full justify-center sm:w-auto" onClick={() => onPick(pick(workflow, latest))}>
                        Select v{latest.version}
                        <span className="sr-only">, latest available version of {label}</span>
                        {latest.id === currentVersionId && (
                            <Badge tone="neutral" className="ml-2">
                                Current
                            </Badge>
                        )}
                    </Button>
                ) : (
                    <span className="text-xs text-muted-foreground">No available version</span>
                )}
            </div>

            {latest && <p className="mt-1 pl-6 text-xs text-muted-foreground">Latest available version</p>}

            {older.length > 0 && (
                <Collapsible open={olderOpen} onOpenChange={setOlderOpen} className="mt-2 pl-6">
                    <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" aria-label={`${olderOpen ? "Hide" : "Show"} older versions of ${label}`}>
                            <ChevronDown className={`mr-1 h-4 w-4 transition-transform ${olderOpen ? "rotate-180" : ""}`} aria-hidden />
                            Older versions ({older.length})
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="mt-2 flex flex-wrap gap-2" aria-label={`Older available versions of ${label}`}>
                            {older.map(version => (
                                <Button
                                    key={version.id}
                                    type="button"
                                    variant={version.id === currentVersionId ? "secondary" : "outline"}
                                    size="sm"
                                    className="h-8"
                                    onClick={() => onPick(pick(workflow, version))}
                                >
                                    Select v{version.version}
                                    {version.id === currentVersionId && (
                                        <Badge tone="neutral" className="ml-2">
                                            Current
                                        </Badge>
                                    )}
                                </Button>
                            ))}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </li>
    );
}

/**
 * Choosing which published version a campaign slot should run.
 *
 * The deployment dialog answers « where does this version go? », which is the direction
 * the lineage asks in. A campaign whose slot is empty asks the opposite question, and
 * without this step it had no way to be filled from the console: the slot card could only
 * ever offer to redeploy what it already ran. Picking here hands over to the same audited
 * dialog, so the preview, the in-flight policy and the `Deployment` row are unchanged.
 *
 * Only published versions are offered, because a draft is refused by the deployment guard
 * anyway — offering one would move the refusal one click later.
 */
export function PickVersionDialog({
    open,
    onOpenChange,
    slot,
    workflows,
    loading = false,
    error = null,
    currentVersionId,
    workflowCode,
    workflowName,
    onSwitchWorkflow,
    onRetry,
    onPick
}: PickVersionDialogProps) {
    const [query, setQuery] = useState("");
    const deferredQuery = useDeferredValue(query);
    const filteredWorkflows = useMemo(() => {
        if (workflowCode) return (workflows ?? []).filter(workflow => workflow.workflow_code === workflowCode);
        const normalized = deferredQuery.trim().toLocaleLowerCase();
        if (!normalized) return workflows ?? [];
        return (workflows ?? []).filter(workflow => `${workflow.workflow_name} ${workflow.workflow_code}`.toLocaleLowerCase().includes(normalized));
    }, [deferredQuery, workflowCode, workflows]);
    const empty = !loading && !error && workflows?.length === 0;
    const noResults = !loading && !error && Boolean(workflows?.length) && filteredWorkflows.length === 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-4 overflow-hidden p-4 sm:p-6">
                <DialogHeader>
                    <DialogTitle>{workflowCode ? `Deploy a version of ${workflowName || workflowCode} on the ${slot} slot` : `Deploy on the ${slot} slot`}</DialogTitle>
                    <DialogDescription>Select a workflow version. You will review the deployment before it is applied.</DialogDescription>
                </DialogHeader>

                {!workflowCode && !loading && !error && !empty && (
                    <div>
                        <label htmlFor={`workflow-search-${slot}`} className="sr-only">
                            Search workflows
                        </label>
                        <Input
                            id={`workflow-search-${slot}`}
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder="Search workflows by name or code…"
                            icon={Search}
                            autoComplete="off"
                            autoFocus
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground" aria-live="polite">
                            {filteredWorkflows.length} {filteredWorkflows.length === 1 ? "workflow" : "workflows"}
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        Loading workflow versions…
                    </div>
                ) : error ? (
                    <Alert variant="destructive">
                        <AlertTitle>Workflow versions could not be loaded</AlertTitle>
                        <AlertDescription className="mt-1">{error}</AlertDescription>
                        {onRetry && (
                            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
                                Try again
                            </Button>
                        )}
                    </Alert>
                ) : empty ? (
                    <Alert variant="warning">
                        <AlertTitle>No available version yet</AlertTitle>
                        <AlertDescription>
                            A slot runs an immutable version, so a version has to be created from the canvas first.{" "}
                            <Link className="underline" to="/dialer-workflows">
                                Create a workflow
                            </Link>
                            , draw its graph, publish it, then come back here.
                        </AlertDescription>
                    </Alert>
                ) : noResults ? (
                    <div className="flex min-h-40 flex-col items-center justify-center px-4 text-center">
                        <p className="text-sm text-gray-500">No workflow matches “{query.trim()}”</p>
                        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setQuery("")}>
                            Clear search
                        </Button>
                    </div>
                ) : (
                    <ul className="min-h-0 flex-1 divide-y overflow-y-auto rounded-md border" aria-label="Workflow versions">
                        <TooltipProvider>
                            {filteredWorkflows.map(workflow => (
                                <WorkflowRow key={workflow.workflow_code} workflow={workflow} currentVersionId={currentVersionId} onPick={onPick} />
                            ))}
                        </TooltipProvider>
                    </ul>
                )}

                <DialogFooter>
                    {workflowCode && onSwitchWorkflow && (
                        <Button type="button" variant="outline" onClick={onSwitchWorkflow}>
                            Switch to another workflow
                        </Button>
                    )}
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
